import { acceptVisibilityScope, visibilityEpoch } from '../visibility-cache'
import type { z } from 'zod'

import type { DashboardUser } from '../../features/auth/auth-adapter'

type ApiErrorCode =
  'unauthenticated' | 'transport' | 'server' | 'invalid-response' | 'cancelled'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number | null
  readonly reason: string | null

  constructor(
    code: ApiErrorCode,
    message: string,
    status: number | null = null,
    reason: string | null = null
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.reason = reason
  }
}

// No response, overload (a request the platform turned away) or a server
// error: a moment later the same read usually succeeds.
const TRANSIENT_STATUSES = new Set([408, 429, 500, 502, 503, 504])

export function isTransientApiError(reason: unknown) {
  return (
    reason instanceof ApiError &&
    (reason.code === 'transport' ||
      (reason.code === 'server' &&
        reason.status !== null &&
        TRANSIENT_STATUSES.has(reason.status)))
  )
}

/** React Query `retry` for reads: two more attempts after a transient failure. */
export const retryTransientRead = (failureCount: number, reason: unknown) =>
  failureCount < 2 && isTransientApiError(reason)

type ApiClientOptions = {
  baseUrl: string
  getUser: () => DashboardUser | null
  fetchImplementation?: typeof fetch
}

type RequestOptions<T> = Omit<RequestInit, 'body'> & {
  body?: unknown
  responseSchema: z.ZodType<T>
  errorSchema?: z.ZodType<{ message: string }, z.ZodTypeDef, unknown>
}

export function createApiClient({
  baseUrl,
  getUser,
  fetchImplementation = fetch,
}: ApiClientOptions) {
  async function request<T>(
    path: string,
    options: RequestOptions<T>
  ): Promise<T> {
    const {
      body: requestBody,
      responseSchema,
      errorSchema,
      ...requestInit
    } = options
    // A request its caller cancelled (requestInit.signal) is reported as
    // cancelled, never as an outage or an invalid response: the caller drops its
    // own cancellations and every real failure stays observable.
    const cancelled = () =>
      new ApiError('cancelled', 'The request was cancelled')
    const epoch = visibilityEpoch()
    const user = getUser()
    if (!user) {
      throw new ApiError('unauthenticated', 'Authentication is required')
    }

    let token: string
    try {
      token = await user.getIdToken()
    } catch {
      throw new ApiError(
        'unauthenticated',
        'Authentication could not be refreshed'
      )
    }
    if (!token) {
      throw new ApiError(
        'unauthenticated',
        'Authentication could not be refreshed'
      )
    }
    if (requestInit.signal?.aborted) throw cancelled()
    const headers = new Headers(requestInit.headers)
    headers.set('Authorization', `Bearer ${token}`)
    headers.set('Accept', 'application/json')

    let body: string | undefined
    if (requestBody !== undefined) {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(requestBody)
    }

    let response: Response
    try {
      response = await fetchImplementation(
        `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`,
        {
          ...requestInit,
          headers,
          body,
        }
      )
    } catch {
      if (requestInit.signal?.aborted) throw cancelled()
      throw new ApiError('transport', 'The dashboard service is unavailable')
    }

    if (!response.ok) {
      let reason: string | null = null
      if (errorSchema) {
        try {
          const parsed = errorSchema.safeParse(await response.json())
          if (parsed.success) reason = parsed.data.message
        } catch {
          reason = null
        }
      }
      if (requestInit.signal?.aborted) throw cancelled()
      throw new ApiError(
        'server',
        response.status === 401 || response.status === 403
          ? 'The request is not authorized'
          : 'The dashboard service could not complete the request',
        response.status,
        reason
      )
    }

    let payload: unknown
    try {
      payload = await response.json()
    } catch {
      if (requestInit.signal?.aborted) throw cancelled()
      throw new ApiError(
        'invalid-response',
        'The dashboard service returned an invalid response'
      )
    }

    if (epoch !== visibilityEpoch() || user.uid !== getUser()?.uid) {
      throw new ApiError('unauthenticated', 'Dashboard visibility changed')
    }
    const visibilityScope = response.headers.get('X-Flio-Visibility-Scope')
    if (visibilityScope) acceptVisibilityScope(visibilityScope)
    const parsed = responseSchema.safeParse(payload)
    if (!parsed.success) {
      throw new ApiError(
        'invalid-response',
        'The dashboard service returned an invalid response'
      )
    }
    return parsed.data
  }

  return { request }
}
